"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Users, CheckCircle2, Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
// import { Progress } from "@/components/ui/progress"; // replaced by compact example checkboxes
import { useUser } from "@/lib/context/user-context";
import { useI18n } from "@/lib/i18n";
import { useUsage } from "@/contexts/usage-context";
import { FreePlanLimitDialog } from "@/components/course/FreePlanLimitDialog";

type Item = { id: number; title: string; englishExplanation?: string; readers?: number };

export function CourseProgressGrid({ slug, items }: { slug: string; items: Item[] }) {
  const { user } = useUser();
  const [progress, setProgress] = useState<Record<string, string>>({});
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [lockedLessonTitle, setLockedLessonTitle] = useState<string | null>(null);
  const { t } = useI18n();
  const { usageInfo, loading: usageLoading } = useUsage();

  const courseLabel = slug === "most-common-american-idioms"
    ? t("course.detail.courses.idioms.title")
    : t("course.progress.defaultCourseLabel", "Course");

  const userId = user?.id;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!userId) return;
      try {
        const url = `/api/course/progress?userId=${encodeURIComponent(userId)}&slug=${encodeURIComponent(slug)}`;
        const res = await fetch(url, { cache: 'no-store' });
        const json = await res.json();
        if (!cancelled && json && json.all) {
          setProgress(json.all as Record<string, string>);
        }
      } catch (e) {
        // silent
      }
    }
    load();
    return () => { cancelled = true; };
  }, [userId, slug]);

  const completedFor = (id: number) => progress?.[`i:${id}:cmp`] === '1';

  // Build a compact example-completion array for each idiom using ex fields
  const examplesFor = (id: number): boolean[] => {
    try {
      const cntRaw = progress?.[`i:${id}:cnt`];
      let cnt = Number(cntRaw || 0);
      const mask = String(progress?.[`i:${id}:mask`] || '');
      // Determine ex count if cnt is missing
      if (!Number.isFinite(cnt) || cnt <= 0) {
        const keys = Object.keys(progress || {}).filter(k => k.startsWith(`i:${id}:ex:`));
        let maxSeq = 0;
        for (const k of keys) {
          const m = k.match(/ex:(\d+)$/);
          if (m) maxSeq = Math.max(maxSeq, parseInt(m[1]!, 10));
        }
        cnt = Math.max(mask.length || 0, maxSeq || 0, 0);
      }
      if (!cnt || cnt < 1) cnt = 3; // fallback to 3 for a consistent layout

      const arr: boolean[] = [];
      for (let i = 1; i <= cnt; i++) {
        const exVal = (progress as any)?.[`i:${id}:ex:${i}`];
        const done = String(exVal) === '1' || mask.charAt(i - 1) === '1';
        arr.push(done);
      }
      return arr;
    } catch {
      return [];
    }
  };

  const restrictsIdioms = slug === "most-common-american-idioms";
  const isFreePlan = !usageLoading && usageInfo?.planType === 'free';

  const handleLockedItem = (item: Item) => {
    setLockedLessonTitle(item.title);
    setLimitDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setLimitDialogOpen(open);
    if (!open) {
      setLockedLessonTitle(null);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {items.map((it) => {
          const done = completedFor(it.id);
          const exList = examplesFor(it.id);
          const locked = restrictsIdioms && isFreePlan && it.id > 10;
          const lockedCardLabel = t('course.freePlanLimit.lockedCardLabel', 'Upgrade to unlock this lesson');

          const content = (
            <div className="flex items-center space-x-4 p-2">
              <div className="relative flex-shrink-0">
                <Image
                  src="/images/cover.png"
                  alt={`${it.title} cover`}
                  width={80}
                  height={80}
                  className="h-20 w-20 rounded-lg border object-cover"
                />
                <div className="absolute left-1 top-[1px] z-10">
                  <Badge variant="outline" className="rounded bg-white/90 px-1.5 py-0.5 text-[10px]">
                    # {it.id}
                  </Badge>
                </div>
                {done && (
                  <div className="absolute -right-1 -top-1 text-green-600" title={t('course.progress.completedTooltip')}>
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <p className="truncate text-sm text-muted-foreground">{courseLabel}</p>
                  {typeof it.readers === 'number' && (
                    <div className="flex items-center rounded-md bg-black px-2 py-0.5 text-white">
                      <Users className="h-3.5 w-3.5 text-yellow-400" />
                      <span className="ml-1 text-xs font-semibold">{it.readers}</span>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-md font-semibold line-clamp-2">{it.title}</p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {exList.map((isDone, idx) => (
                    <Checkbox
                      key={idx}
                      checked={isDone}
                      disabled
                      aria-label={t('course.progress.exampleAria')
                        .replace('{index}', String(idx + 1))
                        .replace('{status}', isDone ? t('course.progress.status.completed') : t('course.progress.status.todo'))}
                      className="pointer-events-none size-4"
                    />
                  ))}
                </div>
              </div>
            </div>
          );

          return (
            <Card
              key={it.id}
              className={`relative overflow-hidden transition-shadow ${locked ? 'cursor-pointer' : 'hover:shadow-lg'}`}
            >
              {locked ? (
                <button
                  type="button"
                  onClick={() => handleLockedItem(it)}
                  aria-label={`${lockedCardLabel}: ${it.title}`}
                  className="block w-full text-left"
                >
                  {content}
                </button>
              ) : (
                <Link href={`/course/${slug}/${it.id}`} className="block">
                  {content}
                </Link>
              )}
              {locked ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px]">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground/80">
                    <Lock className="size-4" />
                    {t('course.freePlanLimit.lockedBanner', 'Upgrade to unlock')}
                  </span>
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
      <FreePlanLimitDialog
        open={limitDialogOpen}
        onOpenChange={handleDialogChange}
        lockedTitle={lockedLessonTitle || undefined}
      />
    </>
  );
}
